import * as React from 'react';
import { Sport } from 'Src/models/sport';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { MultiSelectCheckboxItem } from 'Src/components/MultiSelectCheckboxes/MultiSelectCheckboxes';
import { ClubWizardErrors } from '../ClubWizard';
import { ClubSport, ClubStaff } from 'Src/models/club';
import { getFullName, getName } from 'Src/helpers/autocomplete/autocomplete';

interface Props {
  onInputChange: (event) => void;
  onSportSelect: (sport: Sport) => void;
  onAgesChange: (index: number) => void;
  onAllAgesChange: () => void;
  onGenderChange: (index: number) => void;
  getSports: (text: string) => any;
  name: string;
  description: string;
  sport: Sport | ClubSport;
  ageItems: MultiSelectCheckboxItem[];
  genderItems: MultiSelectCheckboxItem[];
  errors: ClubWizardErrors;
  coaches: ClubStaff[];
  membersOfStaff: ClubStaff[];
  setCoaches: (coaches: ClubStaff[]) => void;
  setMembersOfStaff: (membersOfStaff: ClubStaff[]) => void;
  getCoachesSearchFunction: (text: string) => Promise<any>;
  getMembersOfStaffSearchFunction: (text: string) => Promise<any>;
  isActive: boolean;
}

const renderChip = (
  item: MultiSelectCheckboxItem,
  index: number,
  onChange: (index: number) => void,
  disabled = false
) => (
  <button
    key={`${item.name}_${index}`}
    type="button"
    className={`clubWizardDsChip ${item.isChecked ? 'isSelected' : ''}`}
    disabled={disabled}
    onClick={() => onChange(index)}
  >
    {item.isChecked && <span>✓</span>}
    {item.name}
  </button>
);

const StaffChip = ({ person, onRemove }: { person: ClubStaff; onRemove: () => void }) => (
  <span className="clubWizardDsPersonChip">
    <span>{getFullName(person)}</span>
    <button type="button" onClick={onRemove} aria-label="Remove">
      ×
    </button>
  </span>
);

export function ClubWizardSummaryStep(props: Props) {
  const {
    onInputChange,
    name,
    description,
    sport,
    getSports,
    onSportSelect,
    onAgesChange,
    onAllAgesChange,
    onGenderChange,
    ageItems,
    genderItems,
    membersOfStaff,
    coaches,
    setCoaches,
    setMembersOfStaff,
    errors,
    getCoachesSearchFunction,
    getMembersOfStaffSearchFunction,
    isActive
  } = props;

  const isSportExist = typeof sport !== 'undefined';
  const indexedAges = ageItems.map((item, index) => ({ item, index }));
  const ageStageDefinitions = [
    { key: 'early', label: 'Early years', years: 'N1–Reception', values: ['N1', 'N2', 'Reception'] },
    { key: 'ks1', label: 'KS1', years: 'Y1–Y2', values: ['Y1', 'Y2'] },
    { key: 'ks2', label: 'KS2', years: 'Y3–Y6', values: ['Y3', 'Y4', 'Y5', 'Y6'] },
    { key: 'ks3', label: 'KS3', years: 'Y7–Y9', values: ['Y7', 'Y8', 'Y9'] },
    { key: 'ks4', label: 'KS4', years: 'Y10–Y11', values: ['Y10', 'Y11'] },
    { key: 'ks5', label: 'KS5', years: 'Y12–Y13', values: ['Y12', 'Y13'] },
    { key: 'further', label: 'Further education', years: 'Y14–Post-19', values: ['Y14', 'Post-19'] }
  ];
  const groupedAgeIndexes = new Set<number>();
  const ageSections = ageStageDefinitions
    .map(stage => {
      const items = indexedAges.filter(({ item, index }) => {
        const matches = stage.values.indexOf(item.name) !== -1;
        if (matches) groupedAgeIndexes.add(index);
        return matches;
      });
      return { ...stage, items };
    })
    .filter(stage => stage.items.length > 0);
  const otherAges = indexedAges.filter(({ index }) => !groupedAgeIndexes.has(index));
  if (otherAges.length > 0) {
    ageSections.push({
      key: 'other',
      label: ageSections.length ? 'Other' : 'Age groups',
      years: '',
      values: [],
      items: otherAges
    });
  }

  return (
    <div className="clubWizardDsStep">
      <section className="clubWizardDsSection">
        <div className="clubWizardDsSection__head">
          <span className="clubWizardDsSection__icon">1</span>
          <div>
            <h3>Club details</h3>
            <p>Name the club and choose who it is for.</p>
          </div>
        </div>

        <div className="clubWizardDsGrid clubWizardDsGrid--two">
          <label className="clubWizardDsField clubWizardDsField--name">
            <span>
              Club name <i className="clubWizardDsRequired">*</i>
            </span>
            <input
              type="text"
              onChange={onInputChange}
              value={name}
              name="name"
              placeholder="e.g. Tuesday Football Club"
            />
            {errors.name && <em>Required</em>}
          </label>

          <label className="clubWizardDsField clubWizardDsField--select">
            <span>
              Activity / sport <i className="clubWizardDsRequired">*</i>
            </span>
            <div className="clubWizardDsAutocomplete">
              <Autocomplete
                searchFunction={getSports}
                getElementTitle={getName}
                customClass="mFullWidth"
                defaultItem={sport}
                placeholder="Start typing the activity name"
                onSelect={onSportSelect}
                disabled={isActive}
              />
              <span className="clubWizardDsSelectChevron" aria-hidden="true" />
            </div>
            {errors.sport && <em>Required</em>}
          </label>
        </div>

        <label className="clubWizardDsField">
          <span>Description</span>
          <textarea
            onChange={onInputChange}
            value={description}
            name="description"
            placeholder="Short summary shown to families when booking."
          />
        </label>

        <div className="clubWizardDsField">
          <span>
            Gender <i className="clubWizardDsRequired">*</i>
          </span>
          <div className="clubWizardDsChipRow">
            {genderItems.map((item, index) => renderChip(item, index, onGenderChange, !isSportExist || isActive))}
          </div>
          {errors.gender && <em>Required</em>}
        </div>

        <div className="clubWizardDsField">
          <span className="clubWizardDsField__head">
            <span>Age groups</span>
            <button type="button" onClick={onAllAgesChange}>
              {ageItems.some(item => item.isChecked) ? 'Clear all' : 'Select all'}
            </button>
          </span>
          <div className="clubWizardDsAgeStages">
            {ageSections.map(stage => (
              <section className="clubWizardDsAgeStage" key={stage.key}>
                <div className="clubWizardDsAgeStage__head">
                  <strong>{stage.label}</strong>
                  {stage.years && <span>{stage.years}</span>}
                </div>
                <div className="clubWizardDsChipRow">
                  {stage.items.map(({ item, index }) => renderChip(item, index, onAgesChange))}
                </div>
              </section>
            ))}
          </div>
          <small>Leave all unticked to open the club to every age group.</small>
        </div>
      </section>

      <section className="clubWizardDsSection">
        <div className="clubWizardDsSection__head">
          <span className="clubWizardDsSection__icon">2</span>
          <div>
            <h3>Staffing</h3>
            <p>Add coaches and members of staff who can see or manage the club.</p>
          </div>
        </div>

        <div className="clubWizardDsGrid clubWizardDsGrid--two">
          <div className="clubWizardDsField">
            <span>Leader / Coach</span>
            <div className="clubWizardDsAutocomplete">
              <Autocomplete
                searchFunction={text => getCoachesSearchFunction(text)}
                getElementTitle={getFullName}
                customClass="mFullWidth"
                defaultItem={undefined}
                disabled={!isSportExist}
                placeholder="Search coaches"
                onSelect={coach => setCoaches([...coaches, coach])}
              />
            </div>
            <div className="clubWizardDsPersonChips">
              {coaches.map(coach => (
                <StaffChip
                  key={coach.id}
                  person={coach}
                  onRemove={() => setCoaches(coaches.filter(c => c.id !== coach.id))}
                />
              ))}
            </div>
          </div>

          <div className="clubWizardDsField">
            <span>Member of staff</span>
            <div className="clubWizardDsAutocomplete">
              <Autocomplete
                searchFunction={text => getMembersOfStaffSearchFunction(text)}
                getElementTitle={getFullName}
                customClass="mFullWidth"
                defaultItem={undefined}
                disabled={!isSportExist}
                placeholder="Search staff"
                onSelect={memberOfStaff => setMembersOfStaff([...membersOfStaff, memberOfStaff])}
              />
            </div>
            <div className="clubWizardDsPersonChips">
              {membersOfStaff.map(memberOfStaff => (
                <StaffChip
                  key={memberOfStaff.id}
                  person={memberOfStaff}
                  onRemove={() => setMembersOfStaff(membersOfStaff.filter(m => m.id !== memberOfStaff.id))}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
